<template>
  <div class="upload-container">
    <!-- Header with right-aligned Upload Button -->
    <div class="header d-flex" style="gap: 15px" >
      <button
          class="af-btn v-btn v-btn--is-elevated v-btn--has-bg theme--light v-size--default"
          :title="showFilters ? 'Hide filters' : 'Show filters'"
          @click.stop="toggleFilters"
      >
      <span class="v-btn__content">
        <i class="v-icon notranslate mdi mdi-tune-variant theme--light"></i>
      </span>
      </button>

      <button class="btn-primary" @click="openDialog">
        <img src="/icons/upload.svg" style="width: 20px;" alt="Upload Icon" class="upload-icon"/>
        <span>{{$t("Upload Lessons")}}</span>
      </button>
    </div>

    <!-- Filters (toggle) -->
    <transition name="slide-fade">
      <div v-show="showFilters" class="end-filters-card">
        <div class="end-filters-header">
          <div class="end-filters-title">{{$t("Filters")}}</div>
          <div class="end-filters-meta">
            <button class="btn-secondary small" @click="clearFilters" :disabled="!isFiltering">{{$t("Clear")}}</button>
          </div>
        </div>

        <div class="end-filter-group">
          <div class="end-filter-label">{{$t("Year")}}</div>
          <div class="btn-chip-group">
            <button
                v-for="y in availableYears"
                :key="y"
                class="btn-chip"
                :class="{ active: filters.yearsSelected.includes(y) }"
                @click="toggleFilter('yearsSelected', y)"
            >
              {{ y }}
            </button>
          </div>
        </div>

        <div class="end-filter-group">
          <div class="end-filter-label">{{$t("Semester")}}</div>
          <div class="btn-chip-group">
            <button
                v-for="s in availableSemesters"
                :key="s"
                class="btn-chip"
                :class="{ active: filters.semestersSelected.includes(s) }"
                @click="toggleFilter('semestersSelected', s)"
            >
              {{ s }}
            </button>
          </div>
        </div>

        <div class="end-filter-group">
          <div class="end-filter-label">{{ $t('education_faculty_name') }}</div>
          <div class="btn-chip-group">
            <button
                v-for="f in availableFaculties"
                :key="f.value"
                class="btn-chip"
                :class="{ active: filters.facultiesSelected.includes(f.value) }"
                @click="toggleFilter('facultiesSelected', f.value)"
            >
              {{ f.label }}
            </button>
          </div>
        </div>

        <div class="end-filter-group">
          <div class="end-filter-label">{{ $t('education_type_name') }}</div>
          <div class="btn-chip-group">
            <button
                v-for="t in availableTypes"
                :key="t.value"
                class="btn-chip"
                :class="{ active: filters.typesSelected.includes(t.value) }"
                @click="toggleFilter('typesSelected', t.value)"
            >
              {{ t.label }}
            </button>
          </div>
        </div>

        <div class="end-filter-group">
          <div class="end-filter-label">{{ $t('education_language_name') }}</div>
          <div class="btn-chip-group">
            <button
                v-for="l in availableLanguages"
                :key="l.value"
                class="btn-chip"
                :class="{ active: filters.languagesSelected.includes(l.value) }"
                @click="toggleFilter('languagesSelected', l.value)"
            >
              {{ l.label }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Upload Modal Backdrop & Card -->
    <transition name="fade">
      <div v-if="dialog" class="modal-backdrop" @click.self="closeDialog">
        <transition name="slide-down">
          <div class="modal-card">
            <div class="modal-header">
              <h2 class="modal-title">{{$t("Upload Lessons")}}</h2>

              <button class="close-btn" @click="closeDialog" aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <form @submit.prevent="onUpload" class="modal-form">
              <div class="form-group">
                <label class="label">Google Sheet URL</label>
                <input
                    v-model="sheetUrl"
                    type="url"
                    placeholder="https://..."
                    class="input-field"
                    required
                />
              </div>

              <!-- All dropdowns on new lines -->
              <div class="form-row">
                <!-- Year -->
                <div class="form-group">
                  <label class="label">{{$t("Year")}}</label>
                  <div class="dropdown">
                    <div class="dropdown-toggle" @click="toggleYearDropdown($event)">
                      {{ year || $t("Select year") }}
                      <span class="arrow">▾</span>
                    </div>
                    <div v-show="showYearDropdown" class="dropdown-menu" @click.stop>
                      <div
                          v-for="y in years"
                          :key="y"
                          class="dropdown-item"
                          @click="selectYear(y)"
                      >
                        {{ y }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Semester -->
                <div class="form-group">
                  <label class="label">{{$t("Semester")}}</label>
                  <div class="dropdown">
                    <div class="dropdown-toggle" @click="toggleSemDropdown($event)">
                      {{ semester || $t("Select semester") }}
                      <span class="arrow">▾</span>
                    </div>
                    <div v-show="showSemDropdown" class="dropdown-menu" @click.stop>
                      <div
                          v-for="s in semesters"
                          :key="s"
                          class="dropdown-item"
                          @click="selectSemester(s)"
                      >
                        {{ s }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Education Type -->
                <div class="form-group">
                  <label class="label">{{$t("Education Type")}}</label>
                  <div class="dropdown">
                    <div class="dropdown-toggle" @click="toggleTypeDropdown($event)">
                      {{ selectedTypeLabel || $t("Select education type") }}
                      <span class="arrow">▾</span>
                    </div>
                    <div v-show="showTypeDropdown" class="dropdown-menu" @click.stop>
                      <div
                          v-for="t in educationTypes"
                          :key="t.id"
                          class="dropdown-item"
                          @click="selectType(t.id)"
                      >
                        {{ getName(t) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Education Language -->
                <div class="form-group">
                  <label class="label">{{$t("Education Language")}}</label>
                  <div class="dropdown">
                    <div class="dropdown-toggle" @click="toggleLangDropdown($event)">
                      {{ selectedLangLabel || $t("Select education language") }}
                      <span class="arrow">▾</span>
                    </div>
                    <div v-show="showLangDropdown" class="dropdown-menu" @click.stop>
                      <div
                          v-for="l in educationLanguages"
                          :key="l.id"
                          class="dropdown-item"
                          @click="selectLang(l.id)"
                      >
                        {{ getName(l) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Education Faculty (with search) -->
                <div class="form-group" style="flex:1">
                  <label class="label">{{$t("Education Faculty")}}</label>
                  <div class="dropdown">
                    <div class="dropdown-toggle" @click="toggleFacultyDropdown($event)">
                      {{ selectedFacultyLabel || $t("Select faculty") }}
                      <span class="arrow">▾</span>
                    </div>
                    <div v-show="showFacultyDropdown" class="dropdown-menu" @click.stop>
                      <div class="dropdown-search">
                        <input
                            v-model="facultyQuery"
                            type="text"
                            class="input-field"
                            :placeholder="$t('Search faculty...')"
                            @click.stop
                        />
                      </div>

                      <div
                          v-for="f in filteredFaculties"
                          :key="f.id"
                          class="dropdown-item"
                          @click="selectFaculty(f.id)"
                      >
                        {{ getFacultyName(f) }}
                      </div>

                      <div v-if="!filteredFaculties.length" class="dropdown-empty">
                        No results
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-actions">
                <button type="button" class="btn-secondary" @click="closeDialog">
                  {{$t('Cancel')}}
                </button>
                <button
                    type="submit"
                    class="btn-primary"
                    :disabled="!canSubmit"
                >
                  <span v-if="!loading">{{$t("Upload")}}</span>
                  <svg v-else class="spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <circle class="path" cx="12" cy="12" r="10" fill="none" stroke-width="4"/>
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </transition>
      </div>
    </transition>

    <!-- History Table Card -->
    <div class="table-container">
      <table class="table">
        <thead>
        <tr>
          <th>{{$t('Year')}}</th>
          <th>{{$t('Semester')}}</th>
          <th>{{$t('Created At')}}</th>
          <th>{{$t('Lessons')}}</th>
          <th>{{ $t('education_faculty_name') }}</th>
          <th>{{ $t('education_type_name') }}</th>
          <th>{{ $t('education_language_name') }}</th>
          <th class="actions-col">{{$t("Actions")}}</th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="h in filteredHistories"
            :key="h.id"
            class="clickable-row"
            @click="goToLessons(h.id)"
        >
          <td>{{ h.year }}</td>
          <td>{{ h.semester + ' - semester' }}</td>
          <td>{{ formatDate(h.createdAt) }}</td>
          <td>{{ h.lessonCount }}</td>
          <td>{{ h.education_faculty_name }}</td>
          <td>{{ h.education_type_name }}</td>
          <td>{{ h.education_language_name }}</td>

          <td class="actions-cell" @click.stop>
            <button
                class="tc-act tc-act--delete"
                :title="deletingId === h.id ? $t('Deleting') : $t('Delete upload')"
                :disabled="deletingId === h.id"
                @click="openDelete(h)"
            >
              <template v-if="deletingId === h.id">
                <svg class="mini-spinner" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" fill="none" stroke-width="3" class="path"></circle>
                </svg>
              </template>
              <template v-else>
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
              </template>
            </button>
          </td>
        </tr>

        <tr v-if="!filteredHistories.length">
          <td colspan="8" class="no-data">{{$t("No upload history found.")}}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- === INLINE DELETE MODAL (no component) === -->
    <transition name="fade">
      <div
          v-if="deleteDialog"
          class="confirm-backdrop"
          @click.self="closeDelete"
      >
        <transition name="confirm-pop">
          <div class="confirm-card" role="dialog" aria-modal="true">


            <div class="confirm-header d-flex justify-space-between " >
              <div class="d-flex align-center" style="gap:10px">
                <div class="confirm-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
                <div>
                  <h3 class="confirm-title">{{$t("Delete upload")}}</h3>
                </div>
              </div>

              <button class="close-btn" @click="closeDelete" :disabled="isDeleting" aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <div v-if="deleteTarget" class="confirm-meta">
              <div><strong>ID:</strong> {{ deleteTarget.id }}</div>
              <div><strong>{{ $t("Year") }}:</strong> {{ deleteTarget.year }}</div>
              <div><strong>{{ $t("Semester") }}:</strong> {{ deleteTarget.semester }}</div>
              <div><strong>{{ $t('Lessons') }}}:</strong> {{ deleteTarget.lessonCount }}</div>
              <div><strong>{{ $t('education_faculty_name') }}:</strong> {{ deleteTarget.education_faculty_name || '—' }}</div>
              <div><strong>{{ $t('education_type_name') }}:</strong> {{ deleteTarget.education_type_name || '—' }}</div>
              <div><strong>{{ $t('education_language_name') }}:</strong> {{ deleteTarget.education_language_name || '—' }}</div>
            </div>

            <div class="confirm-actions">
              <button class="btn-secondary" type="button" @click="closeDelete" :disabled="isDeleting">{{$t('Cancel')}}</button>
              <button class="btn-danger" type="button" @click="doDelete">
                <span v-if="!isDeleting">{{$t("Delete")}}</span>
                <svg v-else class="mini-spinner" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" fill="none" stroke-width="3" class="path"></circle>
                </svg>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
    <!-- === /INLINE DELETE MODAL === -->
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LessonUploadDialog',
  data() {
    return {
      dialog: false,
      sheetUrl: '',
      year: '',
      semester: '',
      years: this.generateYears(),
      semesters: [1, 2],
      histories: [],
      loading: false,

      // dropdown visibility
      showYearDropdown: false,
      showSemDropdown: false,
      showTypeDropdown: false,
      showLangDropdown: false,
      showFacultyDropdown: false,

      // dropdown data
      educationTypes: [],
      educationLanguages: [],
      educationFaculties: [],
      loadingTypes: false,
      loadingLanguages: false,
      loadingFaculties: false,

      // selections (null, not empty strings)
      selectedTypeId: null,
      selectedLangId: null,
      selectedFacultyId: null,

      // faculty search
      facultyQuery: '',

      // local table filters (chips)
      filters: {
        yearsSelected: [],
        semestersSelected: [],
        facultiesSelected: [], // normalized strings, "__NULL__" for nulls
        typesSelected: [],
        languagesSelected: [],
        onlyWithLessons: false,
        onlyWithMeta: false,
      },

      showFilters: false,

      // delete state (row + modal)
      deletingId: null,
      deleteDialog: false,
      deleteTarget: null,
      isDeleting: false,
    };
  },

  computed: {
    canSubmit() {
      return !!(this.sheetUrl && this.year && this.semester && !this.loading);
    },

    selectedTypeLabel() {
      const it = this.educationTypes.find(x => x.id === this.selectedTypeId);
      return it ? this.getName(it) : '';
    },
    selectedLangLabel() {
      const it = this.educationLanguages.find(x => x.id === this.selectedLangId);
      return it ? this.getName(it) : '';
    },
    selectedFacultyLabel() {
      const it = this.educationFaculties.find(x => x.id === this.selectedFacultyId);
      return it ? this.getFacultyName(it) : '';
    },
    filteredFaculties() {
      const q = (this.facultyQuery || '').toLowerCase().trim();
      if (!q) return this.educationFaculties;
      return this.educationFaculties.filter(f => {
        const name = (this.getName(f) || '').toLowerCase();
        return name.includes(q);
      });
    },

    // options for chip filters (built from loaded histories)
    availableYears() {
      const set = new Set((this.histories || []).map(h => String(h.year)));
      return Array.from(set).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
    },
    availableSemesters() {
      const set = new Set((this.histories || []).map(h => Number(h.semester)).filter(Boolean));
      const arr = Array.from(set).sort((a, b) => a - b);
      return arr.length ? arr : [1, 2];
    },
    availableFaculties() {
      const vals = (this.histories || []).map(h => h.education_faculty_name);
      return this._uniqLabelValue(vals);
    },
    availableTypes() {
      const vals = (this.histories || []).map(h => h.education_type_name);
      return this._uniqLabelValue(vals);
    },
    availableLanguages() {
      const vals = (this.histories || []).map(h => h.education_language_name);
      return this._uniqLabelValue(vals);
    },

    isFiltering() {
      const f = this.filters;
      return (
          f.yearsSelected.length ||
          f.semestersSelected.length ||
          f.facultiesSelected.length ||
          f.typesSelected.length ||
          f.languagesSelected.length ||
          f.onlyWithLessons ||
          f.onlyWithMeta
      );
    },

    filteredHistories() {
      const f = this.filters;
      const list = this.histories || [];

      return list.filter(h => {
        const year = String(h.year);
        const sem = Number(h.semester);
        const fac = this._norm(h.education_faculty_name);
        const typ = this._norm(h.education_type_name);
        const lang = this._norm(h.education_language_name);

        if (f.yearsSelected.length && !f.yearsSelected.includes(year)) return false;
        if (f.semestersSelected.length && !f.semestersSelected.includes(sem)) return false;
        if (f.facultiesSelected.length && !f.facultiesSelected.includes(fac)) return false;
        if (f.typesSelected.length && !f.typesSelected.includes(typ)) return false;
        if (f.languagesSelected.length && !f.languagesSelected.includes(lang)) return false;

        if (f.onlyWithLessons && Number(h.lessonCount) <= 0) return false;

        if (f.onlyWithMeta) {
          const ok = !!h.education_faculty_name && !!h.education_type_name && !!h.education_language_name;
          if (!ok) return false;
        }

        return true;
      });
    },
  },

  mounted() {
    this.fetchHistories();
    this.fetchDropdowns();
    document.addEventListener('click', this.closeAllDropdowns);
    document.addEventListener('keydown', this.onKeydownEsc);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeAllDropdowns);
    document.removeEventListener('keydown', this.onKeydownEsc);
  },

  methods: {
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    openDialog() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.showYearDropdown = false;
      this.showSemDropdown = false;
      this.showTypeDropdown = false;
      this.showLangDropdown = false;
      this.showFacultyDropdown = false;
    },

    generateYears() {
      const current = new Date().getFullYear();
      return Array.from({ length: 5 }, (_, i) => String(current - i));
    },

    formatDate(date) {
      if (!date) return '--';
      const d = new Date(date);
      const monthNames = [
        'yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun',
        'iyul', 'avgust', 'sentabr', 'oktyabr', 'noyabr', 'dekabr'
      ];
      const month = monthNames[d.getMonth()];
      const day = d.getDate();
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${day}-${month}, ${hh}:${mm}`;
    },

    async fetchHistories() {
      try {
        const { data } = await axios.get('/lessons/uploads');
        this.histories = data;
      } catch (e) {
        console.error('Failed to load histories', e);
      }
    },

    async fetchDropdowns() {
      this.loadingTypes = this.loadingLanguages = this.loadingFaculties = true;
      try {
        const [tRes, lRes, fRes] = await Promise.all([
          axios.get('/admin/education-type', { params: { page: 0, size: 100 } }),
          axios.get('/admin/education-language', { params: { page: 0, size: 100 } }),
          axios.get('/admin/faculty', { params: { page: 0, size: 100 } }),
        ]);
        this.educationTypes = tRes.data.content || [];
        this.educationLanguages = lRes.data.content || [];
        this.educationFaculties = fRes.data.content || [];
      } finally {
        this.loadingTypes = this.loadingLanguages = this.loadingFaculties = false;
      }
    },

    // dropdown toggles
    toggleYearDropdown(e) {
      e && e.stopPropagation();
      this.showYearDropdown = !this.showYearDropdown;
      this.showSemDropdown = this.showTypeDropdown = this.showLangDropdown = this.showFacultyDropdown = false;
    },
    toggleSemDropdown(e) {
      e && e.stopPropagation();
      this.showSemDropdown = !this.showSemDropdown;
      this.showYearDropdown = this.showTypeDropdown = this.showLangDropdown = this.showFacultyDropdown = false;
    },
    toggleTypeDropdown(e) {
      e && e.stopPropagation();
      this.showTypeDropdown = !this.showTypeDropdown;
      this.showLangDropdown = this.showFacultyDropdown = this.showYearDropdown = this.showSemDropdown = false;
    },
    toggleLangDropdown(e) {
      e && e.stopPropagation();
      this.showLangDropdown = !this.showLangDropdown;
      this.showTypeDropdown = this.showFacultyDropdown = this.showYearDropdown = this.showSemDropdown = false;
    },
    toggleFacultyDropdown(e) {
      e && e.stopPropagation();
      this.showFacultyDropdown = !this.showFacultyDropdown;
      this.showTypeDropdown = this.showLangDropdown = this.showYearDropdown = this.showSemDropdown = false;
    },

    // selections (coerce to Number)
    selectYear(y) {
      this.year = String(y);
      this.showYearDropdown = false;
    },
    selectSemester(s) {
      this.semester = Number(s);
      this.showSemDropdown = false;
    },
    selectType(id) {
      this.selectedTypeId = Number(id);
      this.showTypeDropdown = false;
    },
    selectLang(id) {
      this.selectedLangId = Number(id);
      this.showLangDropdown = false;
    },
    selectFaculty(id) {
      this.selectedFacultyId = Number(id);
      this.showFacultyDropdown = false;
      this.facultyQuery = '';
    },

    // close dropdowns when clicking outside modal
    closeAllDropdowns(e) {
      const modal = this.$el.querySelector('.modal-card');
      if (!modal || (e && modal.contains(e.target))) return;
      this.showYearDropdown = this.showSemDropdown = this.showTypeDropdown = this.showLangDropdown = this.showFacultyDropdown = false;
    },

    getName(it) {
      if (!it) return '';
      const n = it.names || {};
      return n.uz || n.ru || n.en || '';
    },

    getFacultyName(it) {
      if (!it) return '';
      const n = it.names || {};
      let name = n.uz || n.ru || n.en || '';
      return name + ' - ' + (it.mvdir || '');
    },

    async onUpload() {
      if (!this.canSubmit) return;
      this.loading = true;
      try {
        const payload = {
          sheetUrl: String(this.sheetUrl).trim(),
          year: String(this.year),                         // "YYYY"
          semester: Number(this.semester),                 // 1 | 2
          educationFacultyId: this.selectedFacultyId != null ? Number(this.selectedFacultyId) : null,
          educationTypeId: this.selectedTypeId != null ? Number(this.selectedTypeId) : null,
          educationLanguageId: this.selectedLangId != null ? Number(this.selectedLangId) : null,
        };

        await axios.post('/lessons/uploads', payload);

        // Reset form
        this.closeDialog();
        this.sheetUrl = '';
        this.year = '';
        this.semester = '';
        this.selectedTypeId = null;
        this.selectedLangId = null;
        this.selectedFacultyId = null;
        this.facultyQuery = '';
        this.fetchHistories();
      } catch (e) {
        console.error('Upload failed', e);
      } finally {
        this.loading = false;
      }
    },

    goToLessons(uploadId) {
      this.$router.push({ path: `/lesson-data/${uploadId}` });
    },

    // ==== INLINE DELETE MODAL HANDLERS ====
    openDelete(h) {
      this.deleteTarget = h;
      this.deleteDialog = true;
    },
    closeDelete() {
      if (this.isDeleting) return;
      this.deleteDialog = false;
      this.deleteTarget = null;
    },
    async doDelete() {
      if (!this.deleteTarget || this.isDeleting) return;
      this.isDeleting = true;
      this.deletingId = this.deleteTarget.id; // keep row spinner in sync
      try {
        // Matches: @DeleteMapping public ResponseEntity<Void> deleteUpload(@RequestParam Long uploadId)
        await axios.delete('/lessons/uploads', { params: { uploadId: this.deleteTarget.id } });
        // Remove locally
        this.histories = this.histories.filter(x => x.id !== this.deleteTarget.id);
        this.closeDelete();
      } catch (e) {
        console.error('Delete failed', e);
        this.$store.dispatch("SET_SNACKBAR", { text: 'Delete failed: ' + (e?.response?.data?.message || e.message || 'Unknown error'), color: 'error' });
        try { await this.fetchHistories(); } catch (_) {
          //
        }
      } finally {
        this.isDeleting = false;
        this.closeDelete();
      }
    },
    onKeydownEsc(e) {
      if (e && e.key === 'Escape' && this.deleteDialog) this.closeDelete();
    },

    // ==== filter helpers ====
    _norm(v) {
      return v == null || v === '' ? '__NULL__' : String(v);
    },
    _uniqLabelValue(arr) {
      const map = new Map();
      (arr || []).forEach(v => {
        const val = this._norm(v);
        const label = v == null || v === '' ? '—' : String(v);
        if (!map.has(val)) map.set(val, { value: val, label });
      });
      // keep "—" last
      const items = Array.from(map.values()).sort((a, b) => {
        if (a.label === '—') return 1;
        if (b.label === '—') return -1;
        return a.label.localeCompare(b.label);
      });
      return items;
    },

    toggleFilter(key, value) {
      const arr = this.filters[key];
      const idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(value);
    },

    clearFilters() {
      this.filters.yearsSelected = [];
      this.filters.semestersSelected = [];
      this.filters.facultiesSelected = [];
      this.filters.typesSelected = [];
      this.filters.languagesSelected = [];
      this.filters.onlyWithLessons = false;
      this.filters.onlyWithMeta = false;
    },
  },
};
</script>

<style scoped>
/* Container & Header */
.upload-container {
  padding: 16px;
}

.header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

/* Upload Modal Backdrop */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Upload Modal Card */
.modal-card {
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  padding: 24px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  transform-origin: top center;
}

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.slide-down-enter-active, .slide-down-leave-active { transition: transform 0.3s ease; }
.slide-down-enter { transform: translateY(-20px); }
.slide-down-leave-to { transform: translateY(-20px); }

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.modal-title { font-size: 1.25rem; font-weight: 600; color: #1f2937; }

/* Form */
.modal-form { display: flex; flex-direction: column; gap: 16px; }
.form-group { display: flex; flex-direction: column; }
.label { font-size: 0.875rem; margin-bottom: 4px; color: #4b5563; }
.input-field {
  padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 1rem; outline: none;
}
.input-field:focus { border-color: #00274c; }

/* Make ALL dropdowns stack on new lines */
.form-row { display: flex; flex-direction: column; gap: 16px; }

/* Dropdown */
.dropdown { position: relative; }
.dropdown-toggle {
  padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  background: #fff; font-size: 1rem; cursor: pointer;
  display: flex; justify-content: space-between; align-items: center;
}
.arrow { margin-left: 8px; }
.dropdown-menu {
  position: absolute; top: calc(100% + 4px); left: 0; width: 100%;
  background: #fff; border: 1px solid #e5e7eb; border-radius: 8px;
  max-height: 260px; overflow-y: auto; box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

/* Faculty search field inside dropdown */
.dropdown-search { padding: 8px; border-bottom: 1px solid #e5e7eb; }
.dropdown-search .input-field {
  width: 100%; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.95rem; padding: 8px 10px;
}
.dropdown-empty { padding: 10px 12px; color: #6b7280; font-size: 0.9rem; }
.dropdown-item { padding: 8px 12px; cursor: pointer; font-size: 1rem; color: #374151; }
.dropdown-item:hover { background: #f3f4f6; }

/* Actions */
.modal-actions {
  display: flex; justify-content: flex-end; gap: 12px; margin-top: 8px;
}
.btn-primary {
  background: #4f46e5; color: #fff; padding: 8px 16px; border: none; border-radius: 8px;
  font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
  transition: background 0.2s;
}
.btn-primary:hover:not(:disabled) { background: #4338ca; }
.btn-primary:disabled { background: #a5b4fc; cursor: not-allowed; }
.btn-secondary {
  background: #e5e7eb; color: #374151; padding: 10px 16px; border: none; border-radius: 8px;
  font-weight: 600; cursor: pointer; transition: background 0.2s;
}
.btn-secondary:hover { background: #d1d5db; }
.btn-secondary.small { padding: 6px 10px; font-size: 0.85rem; border-radius: 6px; }

/* Danger button */
.btn-danger {
  background: #ef4444; color: #fff; padding: 10px 16px; border: none; border-radius: 8px;
  font-weight: 700; cursor: pointer; transition: background .2s ease;
}
.btn-danger:hover:not(:disabled) { background: #dc2626; }
.btn-danger:disabled { opacity: .7; cursor: not-allowed; }

/* Spinner */
.spinner { animation: spin 1s linear infinite; height: 20px; width: 20px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Table */
.table-container { margin-top: 24px; overflow-x: auto; }
.table {
  width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border-radius: 8px;
  overflow: hidden; box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
}
.table th, .table td { padding: 12px 16px; text-align: left; font-size: 0.875rem; }
.table th { background: #f9fafb; color: #6b7280; font-weight: 600; }
.table tr:nth-child(even) td { background: #f3f4f6; }
.actions-col { width: 90px; }

/* Clickable row */
.clickable-row { cursor: pointer; transition: background 0.2s; }
.clickable-row:hover td { background: #eef2ff; }
.no-data { padding: 16px; text-align: center; color: #9ca3af; }

/* Row action buttons */
.actions-cell { display: flex; gap: 6px; align-items: center; }
.tc-act {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: none; color: #475569; cursor: pointer; transition: all .15s;
}
.tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }
.tc-act:disabled { opacity: .6; cursor: not-allowed; }

/* Mini spinner for delete */
.mini-spinner { width: 18px; height: 18px; animation: spin 1s linear infinite; }
.mini-spinner .path { stroke: currentColor; opacity: .85; }

/* Close button (used in both modals) */
.close-btn {
  background: transparent; border: none; padding: 8px;
  display: flex; align-items: center; justify-content: center; border-radius: 50%;
  cursor: pointer; transition: background-color 0.2s, transform 0.2s; color: #666666;
}
.close-btn:hover { background-color: rgba(0, 0, 0, 0.05); transform: scale(1.1); color: #333333; }

/* Filters panel at bottom */
.end-filters-card {
  background: #fff; border-radius: 8px; box-shadow: 0 6px 12px rgba(0,0,0,0.05);
  padding: 16px; margin-top: 16px; display: flex; flex-direction: column; gap: 14px;
}
.end-filters-header { display: flex; justify-content: space-between; align-items: center; }
.end-filters-title { font-size: 1rem; font-weight: 700; color: #111827; }
.end-filters-meta { display: flex; gap: 10px; align-items: center; color: #6b7280; font-size: 0.9rem; }
.end-filter-group { display: flex; flex-direction: column; gap: 8px; }
.end-filter-label { font-size: 0.875rem; font-weight: 600; color: #4b5563; }
.btn-chip-group { display: flex; flex-wrap: wrap; gap: 8px; }
.btn-chip {
  background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; padding: 6px 10px;
  font-size: 0.9rem; border-radius: 999px; cursor: pointer; transition: all 0.15s ease;
}
.btn-chip:hover { background: #e5e7eb; }
.btn-chip.active { background: #4f46e5; color: #fff; border-color: #4f46e5; }

/* Slide + fade for the filters panel */
.slide-fade-enter-active, .slide-fade-leave-active { transition: all 220ms ease; }
.slide-fade-enter, .slide-fade-leave-to { opacity: 0; transform: translateY(8px); }

/* Filters toggle button */
.af-btn {
  border-radius: 12px !important; background: #fff !important; border: 1px solid var(--af-border) !important;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06) !important; transition: transform .15s ease, box-shadow .2s ease;
  min-width: 44px !important; width: 44px !important; height: 44px !important; padding: 0 !important;
}
.af-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(15, 23, 42, .10) !important; }

/* ===== INLINE DELETE MODAL ===== */
.confirm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.4);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100; /* above upload modal */
}
.confirm-card {
  background: #ffffff;
  border: 1px solid rgba(15,23,42,0.07);
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  padding: 18px 18px 14px;
  box-shadow: 0 26px 70px rgba(2,6,23,.22), 0 8px 20px rgba(2,6,23,.08);
  position: relative;
}
.confirm-pop-enter-active, .confirm-pop-leave-active { transition: all .18s cubic-bezier(.2,.7,.3,1); }
.confirm-pop-enter { opacity: 0; transform: translateY(8px) scale(.98); }
.confirm-pop-leave-to { opacity: 0; transform: translateY(8px) scale(.98); }

.confirm-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.confirm-icon {
  width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center;
  background: #fef2f2; color: #b91c1c; border: 1px solid #fee2e2;
}
.confirm-title { font-weight: 600; color: #0f172a; font-size: 1.05rem; }
.confirm-subtitle { color: #6b7280; font-size: .9rem; margin-top: 2px; }

.confirm-meta {
  display: grid; grid-template-columns: repeat(auto-fit,minmax(160px,1fr));
  gap: 6px 12px; font-size: 0.92rem; color: #374151;
  background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 12px; margin-bottom: 10px;
}
.confirm-check { display: flex; align-items: center; gap: 8px; color: #374151; margin: 6px 0 2px; }

.confirm-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }

/* ══════════════════════════════════════════════════════════════
   APPLE FONT STACK
   ══════════════════════════════════════════════════════════════ */
.upload-container {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
}
.input-field,
.dropdown-toggle,
.dropdown-item,
.btn-primary,
.btn-secondary,
.btn-danger,
.btn-chip,
.label,
.modal-title,
.table,
.confirm-title,
.confirm-meta {
  font-family: inherit;
}

/* ══════════════════════════════════════════════════════════════
   DARK MODE
   ══════════════════════════════════════════════════════════════ */

/* ── Table ── */
.theme--dark .table {
  background: #1a1b23;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}
.theme--dark .table th {
  background: #1a1b23;
  color: #8b8d9e;
  border-bottom: 1px solid #2a2c3a;
}
.theme--dark .table td {
  color: #d5d6de;
  border-bottom: 1px solid #1e1f2d;
}
.theme--dark .table tr:nth-child(even) td {
  background: #161720;
}
.theme--dark .clickable-row:hover td {
  background: #22232e;
}
.theme--dark .no-data {
  color: #606275;
}

/* ── Upload Modal ── */
.theme--dark .modal-card {
  background: #1a1b23;
  border: 1px solid #2a2c3a;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
}
.theme--dark .modal-title {
  color: #e4e5eb;
}
.theme--dark .label {
  color: #8b8d9e;
}
.theme--dark .input-field {
  background: #22232e;
  border-color: #2a2c3a;
  color: #d5d6de;
}
.theme--dark .input-field:focus {
  border-color: #ffd451;
}
.theme--dark .input-field::placeholder {
  color: #606275;
}

/* ── Dropdowns ── */
.theme--dark .dropdown-toggle {
  background: #22232e;
  border-color: #2a2c3a;
  color: #d5d6de;
}
.theme--dark .dropdown-menu {
  background: #1a1b23;
  border-color: #2a2c3a;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}
.theme--dark .dropdown-item {
  color: #d5d6de;
}
.theme--dark .dropdown-item:hover {
  background: #22232e;
}
.theme--dark .dropdown-search {
  border-bottom-color: #2a2c3a;
}
.theme--dark .dropdown-empty {
  color: #606275;
}

/* ── Buttons ── */
.theme--dark .btn-secondary {
  background: #22232e;
  color: #d5d6de;
  border: 1px solid #2a2c3a;
}
.theme--dark .btn-secondary:hover {
  background: #33354a;
}

/* ── Close button ── */
.theme--dark .close-btn {
  color: #8b8d9e;
}
.theme--dark .close-btn:hover {
  background-color: rgba(255, 255, 255, 0.06);
  color: #e4e5eb;
}

/* ── Filters panel ── */
.theme--dark .end-filters-card {
  background: #1a1b23;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}
.theme--dark .end-filters-title {
  color: #e4e5eb;
}
.theme--dark .end-filters-meta {
  color: #8b8d9e;
}
.theme--dark .end-filter-label {
  color: #8b8d9e;
}
.theme--dark .btn-chip {
  background: #22232e;
  color: #d5d6de;
  border-color: #2a2c3a;
}
.theme--dark .btn-chip:hover {
  background: #33354a;
}
.theme--dark .btn-chip.active {
  background: #ffd451;
  color: #111318;
  border-color: #ffd451;
}

/* ── Filter toggle button ── */
.theme--dark .af-btn {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2) !important;
}
.theme--dark .af-btn:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3) !important;
}
.theme--dark .af-btn .v-icon {
  color: #d5d6de !important;
}

/* ── Row action buttons ── */
.theme--dark .tc-act {
  border-color: #2a2c3a;
  color: #9ca3af;
  background: none;
}
.theme--dark .tc-act:hover {
  border-color: #ffd451;
  color: #ffd451;
  background: rgba(255,212,81,.06);
}
.theme--dark .tc-act--delete:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239,68,68,.06);
}

/* ── Delete confirmation modal ── */
.theme--dark .confirm-card {
  background: #1a1b23;
  border-color: #2a2c3a;
  box-shadow: 0 26px 70px rgba(0, 0, 0, 0.4), 0 8px 20px rgba(0, 0, 0, 0.2);
}
.theme--dark .confirm-title {
  color: #e4e5eb;
}
.theme--dark .confirm-meta {
  background: #22232e;
  border-color: #2a2c3a;
  color: #d5d6de;
}
.theme--dark .confirm-icon {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

/* ── Spinner ── */
.theme--dark .spinner .path,
.theme--dark .mini-spinner .path {
  stroke: currentColor;
}
</style>
